import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { createMarketAnalysisPost } from '../api/marketAnalysisPostsApi';
import { deletePage, getPages } from '../api/pagesApi';
import { PagesList } from '../components/dashboard/PagesList';
import StatusMessage from '../components/dashboard/StatusMessage';
import { partitionPages } from '../utils/pageGroups';

const initialForm = {
  name: '',
  slug: '',
  category: '',
  title: '',
  excerpt: '',
  author: '',
  date: '',
  featured: false,
};

const MarketAnalysisPostPagesPage = () => {
  const [marketAnalysisPostPages, setMarketAnalysisPostPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState(initialForm);

  const loadPages = async () => {
    const loadedPages = await getPages();
    const { marketAnalysisPostPages: pages } = partitionPages(loadedPages);
    setMarketAnalysisPostPages(pages);
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        await loadPages();
        if (!isMounted) {
          return;
        }
        setError('');
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setMarketAnalysisPostPages([]);
        setError(err.message || 'Failed to load pages.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleCreate = async (event) => {
    event.preventDefault();
    setIsCreating(true);
    setError('');
    setMessage('');

    try {
      await createMarketAnalysisPost(form);
      setForm(initialForm);
      setMessage('Post created successfully.');
      await loadPages();
    } catch (err) {
      setError(err.message || 'Failed to create post.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (page) => {
    if (!window.confirm(`Delete "${page.name}"? This cannot be undone.`)) {
      return;
    }

    setDeletingId(page.id);
    setError('');
    setMessage('');

    try {
      await deletePage(page.id);
      setMessage('Post deleted successfully.');
      await loadPages();
    } catch (err) {
      setError(err.message || 'Failed to delete post.');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading market analysis posts…</p>;
  }

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-6 border-b border-[#e4d9c8] pb-5">
        <Link
          to="/dashboard/pages"
          className="text-[12px] font-medium text-[#785E32] hover:underline"
        >
          ← All pages
        </Link>
        <p className="mt-3 text-[12px] font-medium uppercase tracking-[0.16em] text-[#6b635c]">
          Page group
        </p>
        <h2 className="mt-2 font-[Merriweather] text-[1.5rem] text-[#130705]">
          Market analysis posts
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#6b635c]">
          Create and edit individual market analysis articles. Mark one post as featured to show it in the main hero card.
        </p>
      </header>

      {message ? <StatusMessage type="success" message={message} className="mb-4" /> : null}
      {error ? <StatusMessage type="error" message={error} className="mb-4" /> : null}

      <section className="mb-8 rounded-md border border-[#e4d9c8] bg-white p-5">
        <h3 className="font-[Merriweather] text-lg text-[#130705]">Add new post</h3>
        <form className="mt-4 grid gap-4" onSubmit={handleCreate}>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Internal name</span>
              <input
                className="w-full rounded-md border border-[#d8cfc0] px-3 py-2"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Slug</span>
              <input
                className="w-full rounded-md border border-[#d8cfc0] px-3 py-2"
                value={form.slug}
                onChange={(event) => setForm({ ...form, slug: event.target.value })}
                placeholder="auto-generated if empty"
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Title</span>
            <input
              className="w-full rounded-md border border-[#d8cfc0] px-3 py-2"
              value={form.title}
              onChange={(event) => setForm({ ...form, title: event.target.value })}
              required
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Category</span>
              <input
                className="w-full rounded-md border border-[#d8cfc0] px-3 py-2"
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value })}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Date</span>
              <input
                className="w-full rounded-md border border-[#d8cfc0] px-3 py-2"
                value={form.date}
                onChange={(event) => setForm({ ...form, date: event.target.value })}
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Author</span>
            <input
              className="w-full rounded-md border border-[#d8cfc0] px-3 py-2"
              value={form.author}
              onChange={(event) => setForm({ ...form, author: event.target.value })}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Excerpt</span>
            <textarea
              className="min-h-24 w-full rounded-md border border-[#d8cfc0] px-3 py-2"
              value={form.excerpt}
              onChange={(event) => setForm({ ...form, excerpt: event.target.value })}
            />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-[#4a4340]">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(event) => setForm({ ...form, featured: event.target.checked })}
            />
            Set as featured post
          </label>
          <button
            type="submit"
            disabled={isCreating}
            className="inline-flex w-fit rounded-md border border-[#e4d9c8] bg-[#faf8f4] px-4 py-2 text-sm font-medium text-[#130705] hover:bg-white disabled:opacity-60"
          >
            {isCreating ? 'Creating…' : 'Create post'}
          </button>
        </form>
      </section>

      <PagesList
        pages={marketAnalysisPostPages}
        emptyMessage="No market analysis posts found. Restart the backend to seed them."
        renderActions={(page) => (
          <button
            type="button"
            onClick={() => handleDelete(page)}
            disabled={deletingId === page.id}
            className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-[12px] font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
          >
            {deletingId === page.id ? 'Deleting…' : 'Delete'}
          </button>
        )}
      />
    </div>
  );
};

export default MarketAnalysisPostPagesPage;
